import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { toast } from 'sonner'

import { AUTO_CLOSE_LABEL } from '@czas/shared'

import { ApiHttpError, apiFetch } from '@/lib/api'
import { markLoggedOut } from '@/lib/auth'

export interface WorkSession {
  id: string
  userId: string
  startedAt: string
  endedAt: string | null
  startedLat: number | null
  startedLng: number | null
  startedAccuracyM: number | null
  endedLat: number | null
  endedLng: number | null
  endedAccuracyM: number | null
  startedSource: 'EMPLOYEE_APP' | 'ADMIN_CORRECTION' | 'ADMIN_ADDED'
  endedSource: 'EMPLOYEE_APP' | 'ADMIN_CORRECTION' | 'ADMIN_ADDED' | null
  clientEventId: string | null
  endedClientEventId: string | null
  autoClosed: boolean
  autoClosedAt: string | null
  autoClosedEndedAt: string | null
  autoCloseResolvedAt: string | null
  /** Moment kliknięcia „Koniec pracy" daleko od placówki — i górna granica korekty. */
  remoteEndPromptAt: string | null
  remoteEndPromptDistanceM: number | null
  remoteEndResolvedAt: string | null
  notes: string | null
  durationSeconds: number | null
}

export interface WorkSessionsMeResponse {
  sessions: WorkSession[]
  openSession: WorkSession | null
  unresolvedAutoClosed: WorkSession[]
  /** Zakończenia poza placówką bez podanej godziny wyjścia — niezależne od [from,to]. */
  pendingRemoteEnd: WorkSession[]
}

export interface GpsPayload {
  lat: number
  lng: number
  accuracyM: number
}

export interface WorkSessionClick extends GpsPayload {
  /**
   * Nadawany w chwili KLIKNIĘCIA, nie wysyłki. Gdy odpowiedź zginie po drodze,
   * ponowienie z tym samym id backend rozpozna jako to samo zdarzenie.
   */
  clientEventId: string
}

const WORK_SESSIONS_KEY = ['work-sessions'] as const

/**
 * Start/koniec pracy i wysyłka zaległych zapisów. Krócej niż domyślne 20 s:
 * pracownik stoi z telefonem przy drzwiach, a brak odpowiedzi i tak kończy się
 * odświeżeniem stanu z serwera.
 */
export const WORK_SESSION_REQUEST_TIMEOUT_MS = 10_000

export function myWorkSessionsQueryKey(params?: { from?: string; to?: string }) {
  return [...WORK_SESSIONS_KEY, 'me', params ?? null] as const
}

/**
 * Stan pracy pracownika — jedyne źródło trybu przycisku i licznika na ekranie
 * głównym. Dlatego, inaczej niż reszta aplikacji, odświeża się sam: przy
 * powrocie do aplikacji, po odzyskaniu sieci i co 30 s, gdy karta jest widoczna.
 */
export function useMyWorkSessions(params?: { from?: string; to?: string }) {
  const search = new URLSearchParams()
  if (params?.from) search.set('from', params.from)
  if (params?.to) search.set('to', params.to)
  const qs = search.toString()
  const path = qs ? `/api/work-sessions/me?${qs}` : '/api/work-sessions/me'

  return useQuery<WorkSessionsMeResponse>({
    queryKey: myWorkSessionsQueryKey(params),
    queryFn: () =>
      apiFetch<WorkSessionsMeResponse>(path, {
        timeoutMs: WORK_SESSION_REQUEST_TIMEOUT_MS,
      }),
    staleTime: 30_000,
    // 'always' — przy domyślnym 'online' zdarzenie `offline` wstrzymywało
    // zapytanie, a ekran bez końca pokazywał ostatni znany stan jak aktualny.
    networkMode: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 30_000,
    // 4xx to odpowiedź serwera, nie awaria — ponowienie 401 tylko dublowało
    // zapytanie i opóźniało wylogowanie. Jedna powtórka zostaje dla braku
    // odpowiedzi i 5xx.
    retry: (failureCount, err) =>
      !(err instanceof ApiHttpError && err.status < 500) && failureCount < 1,
    // Dostępność przycisku zależy od świeżości i unieważnienia tych danych,
    // a nie tylko od `data` — ekran musi przerysować się przy każdej zmianie.
    notifyOnChangeProps: 'all',
  })
}

function autoClosedLabel(session: WorkSession): string {
  return session.autoClosedEndedAt
    ? format(new Date(session.autoClosedEndedAt), 'HH:mm')
    : AUTO_CLOSE_LABEL
}

function makeUseWorkSessionMutation(
  type: 'start' | 'end',
  successToast: string,
) {
  return function useWorkSessionMutation() {
    const qc = useQueryClient()
    return useMutation<WorkSession, Error, WorkSessionClick>({
      mutationFn: async ({ clientEventId, lat, lng, accuracyM }) => {
        // Brak `occurredAt`: zapis istnieje dopiero, gdy serwer go potwierdzi,
        // więc czasem zdarzenia jest moment przyjęcia przez API.
        const result = await apiFetch<{ session: WorkSession }>(
          `/api/work-sessions/${type}`,
          {
            method: 'POST',
            body: { clientEventId, lat, lng, accuracyM },
            timeoutMs: WORK_SESSION_REQUEST_TIMEOUT_MS,
          },
        )
        return result.session
      },
      // Kliknięcie nigdy nie może zostać wstrzymane i odegrane później —
      // dostałoby godzinę wznowienia zamiast godziny kliknięcia.
      networkMode: 'always',
      onSuccess: async (session) => {
        if (session.autoClosed && session.autoCloseResolvedAt === null) {
          // Backend tak odpowiada na „Koniec pracy" w sesji, która minęła już
          // swoją granicę auto-zamknięcia — godzina kliknięcia nic nie znaczy.
          toast.info(
            `Ta sesja została już zamknięta automatycznie o ${autoClosedLabel(session)}. Podaj godzinę faktycznego wyjścia.`,
            { duration: 10_000 },
          )
        } else {
          toast.success(successToast)
        }
        void qc.invalidateQueries({ queryKey: ['my-monthly-progress'] })
        // Zwrócona obietnica trzyma mutację w toku, dopóki nie przyjdzie nowy
        // stan — bez tego drugie stuknięcie trafiało w stary tryb przycisku.
        await qc.invalidateQueries({ queryKey: WORK_SESSIONS_KEY })
      },
      onError: (err) => {
        // Po błędzie sieci serwer mógł zapis przyjąć, a po 409 cache na pewno
        // jest nieaktualny — w obu przypadkach prawdę zna tylko serwer.
        void qc.invalidateQueries({ queryKey: WORK_SESSIONS_KEY })
        if (err instanceof ApiHttpError && err.status === 401) markLoggedOut(qc)
      },
    })
  }
}

export const useStartWorkSession = makeUseWorkSessionMutation(
  'start',
  'Praca rozpoczęta',
)
export const useEndWorkSession = makeUseWorkSessionMutation(
  'end',
  'Praca zakończona',
)

export interface ResolveAutoCloseInput {
  sessionId: string
  /** ISO 8601. Pominięte = potwierdzam godzinę nadaną automatycznie. */
  endedAt?: string
}

/**
 * Potwierdzenie albo korekta godziny wyjścia dla sesji domkniętej o 21:30.
 * Przy realnej korekcie backend wysyła push do adminów i dopisuje audit log.
 */
export function useResolveAutoClose() {
  const qc = useQueryClient()
  // `Error`, nie `ApiHttpError`: brak odpowiedzi to `ApiNetworkError` bez
  // `status` — kto czyta status, sprawdza najpierw `instanceof ApiHttpError`.
  return useMutation<WorkSession, Error, ResolveAutoCloseInput>({
    mutationFn: async ({ sessionId, endedAt }) => {
      const res = await apiFetch<{ session: WorkSession }>(
        `/api/work-sessions/${sessionId}/auto-close-resolve`,
        { method: 'POST', body: endedAt !== undefined ? { endedAt } : {} },
      )
      return res.session
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WORK_SESSIONS_KEY })
      qc.invalidateQueries({ queryKey: ['my-monthly-progress'] })
    },
  })
}

export interface ResolveRemoteEndInput {
  sessionId: string
  /** ISO 8601. Pominięte = potwierdzam godzinę kliknięcia. */
  endedAt?: string
}

/**
 * Potwierdzenie albo korekta godziny wyjścia dla sesji zakończonej daleko od
 * placówki. Backend przyjmuje wyłącznie korektę W DÓŁ (do momentu kliknięcia) —
 * dopisanie czasu, którego system nie widział, zostaje decyzją admina.
 */
export function useResolveRemoteEnd() {
  const qc = useQueryClient()
  // `Error` — patrz useResolveAutoClose.
  return useMutation<WorkSession, Error, ResolveRemoteEndInput>({
    mutationFn: async ({ sessionId, endedAt }) => {
      const res = await apiFetch<{ session: WorkSession }>(
        `/api/work-sessions/${sessionId}/remote-end-resolve`,
        { method: 'POST', body: endedAt !== undefined ? { endedAt } : {} },
      )
      return res.session
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WORK_SESSIONS_KEY })
      qc.invalidateQueries({ queryKey: ['my-monthly-progress'] })
    },
    onError: (err) => {
      // 409 znaczy dokładnie „serwer ma inny stan niż mój cache" — najczęściej
      // admin poprawił godzinę, zanim pracownik odpowiedział. Bez refetchu
      // sesja zostaje w `pendingRemoteEnd` i baner zaprasza do modala, który
      // znowu odbije się tym samym błędem.
      if (err instanceof ApiHttpError && err.status === 409) {
        qc.invalidateQueries({ queryKey: WORK_SESSIONS_KEY })
      }
    },
  })
}
